import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ThemeProvider, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useSearchParams } from 'react-router-dom';

import { ResultType } from '../components/results';
import { useAuth } from '../utils/AuthContext';
import { main_theme } from './theme';

export type cat_name_namezh_type = {
  cat: string,
  name: string,
  namezh: string,
}

export type SelectionType = {
  id: string,
  name: string,
  cols: number,
  list: cat_name_namezh_type[] | string[],
};

export type InputsType = Record<string, string>;

const JiangContext = createContext<{
  loading: boolean,
  setLoading: Dispatch<SetStateAction<boolean>>,
  results: ResultType[],
  setResults: Dispatch<SetStateAction<ResultType[]>>,
  selection: SelectionType[],
  inputs_init: InputsType,
  inputs: InputsType,
  inputs_immutable: InputsType,
  setInputs: Dispatch<SetStateAction<InputsType>>,
  setInputsImmutable: Dispatch<SetStateAction<InputsType>>,
  bigger_than_xs: boolean,
  bigger_than_md: boolean,
} | null>(null);

export const JiangProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const { api } = useAuth();

  const [searchParams] = useSearchParams();
  const query = searchParams.toString();

  /* Results loading */
  const [loading, setLoading] = useState<boolean>(false);

  /* Results */
  const [results, setResults] = useState<ResultType[]>([]);
  useEffect(() => {
    if (!query) { setResults([]); return; }
    // console.log('query changed! can call api');
    // console.log(query);
    setLoading(true); // need this here and also in search component because this will set true when user pastes the link instead of pressing the search button
    let payload: Record<string, string> = {}; // Record is same as { [key:string]: string }
    searchParams.forEach((val: string, key: string) => {
      // console.log('val', val);
      // console.log('key', key);
      payload[key] = val
    })

    const fetch_data = async () => {
      const res = await api.post('/api/search', payload);
      // console.log('res', res.data);
      setLoading(false);
      setResults(res.data);
    }
    fetch_data();

  }, [query]);

  /* Selection list setup */
  const [selection, setSelection] = useState<SelectionType[]>([
    { id: 'cat', name: '科別', cols: 3, list: [] },
    { id: 'name', name: '病名', cols: 3, list: [] },
    { id: 'namezh', name: '中文病名', cols: 3, list: [] },
    { id: 'pat', name: '病人階級', cols: 3, list: ['初階', '中階', '高階'] }
  ]);
  useEffect(() => {
    const fetch_opts = async () => {
      const res = await api.get('/api/opts');
      let selection_opts = res.data;
      // console.log('selection opts', selection_opts);
      setSelection(prev =>
        prev.map(item =>
          // item.id in selection_opts
          Object.hasOwn(selection_opts, item.id)
            ? { ...item, list: selection_opts[item.id as keyof typeof selection_opts] }
            : item
        )
      )
    }
    fetch_opts();
  }, []);

  /* Selection input setup */
  const inputs_init = selection.reduce((acc: any, cur) => {
    acc[cur.id] = '';
    return acc;
  }, {}); // blank object of acronym: empty string
  inputs_init['keywords'] = '';
  // console.log('inputs_init', inputs_init);
  const [inputs, setInputs] = useState<InputsType>({ ...inputs_init });
  const [inputs_immutable, setInputsImmutable] = useState<InputsType>({ ...inputs_init });
  // above is for when user changes inputs but we want to keep the original inputs in case users cancels the search

  useEffect(() => {
    searchParams.forEach((val: string, key: string) => {
      // console.log('val', val);
      // console.log('key', key);
      setInputs((prev: any) => ({ ...prev, [key]: val }))
      setInputsImmutable((prev: any) => ({ ...prev, [key]: val }))
    })
  }, [])

  const theme = useTheme();
  const bigger_than_xs = useMediaQuery(theme.breakpoints.up('sm'));
  const bigger_than_md = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <ThemeProvider theme={main_theme}>
      <JiangContext.Provider value={{
        results, setResults,
        selection, inputs_init, inputs, setInputs,
        inputs_immutable, setInputsImmutable,
        loading, setLoading, bigger_than_xs, bigger_than_md
      }}>
        {children}
      </JiangContext.Provider>
    </ThemeProvider>

  )
}

export const useJiang = () => {
  const ctx = useContext(JiangContext);
  if (!ctx) throw new Error('useJiang must be used within JiangProvider');
  return ctx;
}